<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { dealsAPI } from '../api';
import { camelize } from '../store';
import { useCrmStore } from '../store';
import { formatMoney } from '../helpers';

import Button from 'dashboard/components-next/button/Button.vue';
import DealDialog from './DealDialog.vue';

const props = defineProps({
  contactId: { type: [Number, String], default: null },
  contactName: { type: String, default: '' },
  conversationId: { type: [Number, String], default: null },
});

const { t, locale } = useI18n();
const router = useRouter();
const store = useCrmStore();

const deals = ref([]);
const isLoading = ref(false);
const hasError = ref(false);
const dealDialogRef = ref(null);

const stages = computed(() => {
  const map = new Map();
  store.pipelines.forEach(p => p.stages.forEach(s => map.set(s.id, s)));
  return map;
});
const allStages = computed(() => store.pipelines.flatMap(p => p.stages));

const fetchDeals = async () => {
  if (!props.contactId) {
    deals.value = [];
    return;
  }
  isLoading.value = true;
  hasError.value = false;
  try {
    const { data } = await dealsAPI.list({ contact_id: props.contactId });
    deals.value = data.map(d => camelize(d));
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const stageOf = deal => stages.value.get(deal.stageId);

const openNew = () =>
  dealDialogRef.value?.open({
    defaults: {
      contactId: props.contactId,
      contactName: props.contactName,
      conversationId: props.conversationId,
      name: props.contactName,
    },
  });
const openDeal = deal => dealDialogRef.value?.open({ deal });

const goToBoard = () => router.push({ name: 'wasapi_crm_board' });

onMounted(async () => {
  if (!store.pipelines.length) await store.fetchPipelines();
  fetchDeals();
});
watch(() => props.contactId, fetchDeals);
</script>

<template>
  <div class="flex flex-col gap-2">
    <div v-if="isLoading" class="text-xs text-n-slate-11">
      {{ t('WASAPI_CRM.PANEL.LOADING') }}
    </div>
    <button
      v-else-if="hasError"
      type="button"
      class="text-xs text-left text-n-ruby-11 hover:underline"
      @click="fetchDeals"
    >
      {{ t('WASAPI_CRM.PANEL.LOAD_ERROR') }}
    </button>
    <div v-else-if="!deals.length" class="text-xs text-n-slate-11">
      {{ t('WASAPI_CRM.PANEL.EMPTY') }}
    </div>
    <button
      v-for="deal in deals"
      :key="deal.id"
      type="button"
      class="flex flex-col gap-1 p-2 text-left rounded-lg outline outline-1 outline-n-weak hover:outline-n-slate-7"
      @click="openDeal(deal)"
    >
      <div class="flex items-center justify-between gap-2">
        <span class="text-sm font-medium truncate text-n-slate-12">{{
          deal.name
        }}</span>
        <span
          v-if="deal.amountCents"
          class="text-xs font-semibold text-n-teal-11 shrink-0"
        >
          {{ formatMoney(deal.amountCents, deal.currency, locale) }}
        </span>
      </div>
      <div
        v-if="stageOf(deal)"
        class="flex items-center gap-1.5 text-xs text-n-slate-11"
      >
        <span
          class="size-2 rounded-full"
          :style="{ backgroundColor: stageOf(deal).color }"
        />
        {{ stageOf(deal).name }}
      </div>
    </button>
    <div class="flex items-center gap-2 pt-1">
      <Button
        :label="t('WASAPI_CRM.PANEL.NEW')"
        icon="i-lucide-plus"
        size="xs"
        variant="faded"
        color="blue"
        @click="openNew"
      />
      <Button
        :label="t('WASAPI_CRM.PANEL.OPEN_BOARD')"
        size="xs"
        variant="link"
        color="slate"
        @click="goToBoard"
      />
    </div>
    <DealDialog
      ref="dealDialogRef"
      :stages="allStages"
      lock-contact
      @saved="fetchDeals"
      @deleted="fetchDeals"
    />
  </div>
</template>
